import { type ReactNode, useRef } from 'react'

import {
  BarChart,
  downloadRowsCsv,
  downloadRowsXlsx,
  LineChart,
  type RowExport,
  SankeyChart,
  SegmentedControl,
  tableExportFilename,
} from '@/components/ui'
import { exportChartAsPNG } from '@/components/ui/chart/utils/exportChart'
import { useProject } from '@/modules/projects'
import type { ReportChartSpec } from '../../stores/types'
import {
  hasSankeyCycle,
  resolveSankeyLinks,
  sankeyChartHeight,
  sankeyLabelsByName,
} from './chartData'
import { DownloadMenu } from './DownloadMenu'
import { ExpandedChartDialog } from './ExpandedChartDialog'
import { CHART_VIEW_ATTRIBUTE } from './export/chartViews'
import { FlowOfFundsChart } from './FlowOfFundsChart'
import { FlowSummary } from './FlowOfFundsChart/FlowSummary'
import { flowChartHeight } from './FlowOfFundsChart/flowLayout'
import { hasFlowOfFundsRoles } from './FlowOfFundsChart/flowModel'
import {
  FLOW_VIEWS,
  flowForView,
  flowSummaryForView,
} from './FlowOfFundsChart/flowViews'
import { formatCell } from './format'
import {
  chartSpecForView,
  DEFAULT_SANKEY_VIEW,
  emptySankeyViewNote,
  type SankeyView,
  sankeyViewLabel,
} from './sankeyViews'
import { useSankeyView } from './useSankeyView'

const BAR_LINE_HEIGHT = 300

// Room left in the viewport for the chart after the dialog's margin,
// padding and header, so a short chart still fills the expanded view.
const EXPANDED_MIN_HEIGHT_CLASS = 'min-h-[calc(100vh-14rem)]'

interface ChartLayout {
  toolbar?: ReactNode
  expanded?: boolean
  emptyViewNote?: string | null
  view?: SankeyView
}

interface ReportChartProps {
  spec: ReportChartSpec
}

// Adapter from the wire ChartSpec to the shared chart components.
// Anything malformed or unknown degrades to a small data listing or a
// muted placeholder — a report chart must never throw. A real chart gets
// a "Download chart" menu (PNG / CSV / Excel) and an "Expand" button just
// beneath it. The expanded copy shares the picked view with the inline one.
export function ReportChart({ spec }: ReportChartProps) {
  const chartRef = useRef<HTMLDivElement>(null)
  const { views, view, setView, shownSpec } = useSankeyView(spec)
  const viewSwitch = views.length > 0 && (
    <SegmentedControl
      ariaLabel="Which money flows to show"
      options={views.map((option) => ({
        value: option,
        label: sankeyViewLabel(option),
      }))}
      value={view}
      onChange={setView}
    />
  )
  const emptyViewNote =
    (shownSpec.links ?? []).length === 0 ? emptySankeyViewNote(view) : null
  // An empty view draws the whole chart hidden under its note, so the
  // report below does not jump when the view changes.
  const renderShown = (layout: ChartLayout) =>
    renderChart(spec, {
      ...layout,
      emptyViewNote,
      view: emptyViewNote ? DEFAULT_SANKEY_VIEW : view,
    })
  const rendered = renderShown({ toolbar: viewSwitch })
  const exportRows = chartExportRows(shownSpec)
  const viewAttribute = views.length > 0 ? { [CHART_VIEW_ATTRIBUTE]: view } : {}

  return (
    <figure className="space-y-3" data-chart-id={spec.id} {...viewAttribute}>
      <div ref={chartRef}>{rendered}</div>
      {(exportRows || emptyViewNote) && (
        <div className="flex justify-end gap-1">
          <ExpandedChartDialog title={spec.title} toolbar={viewSwitch}>
            {renderShown({ expanded: true })}
          </ExpandedChartDialog>
          {exportRows && (
            <ChartDownloadMenu
              spec={spec}
              exportRows={exportRows}
              chartRef={chartRef}
            />
          )}
        </div>
      )}
    </figure>
  )
}

function ChartDownloadMenu({
  spec,
  exportRows,
  chartRef,
}: {
  spec: ReportChartSpec
  exportRows: RowExport
  chartRef: React.RefObject<HTMLDivElement | null>
}) {
  const project = useProject()
  const baseFilename = tableExportFilename(spec.title, 'chart', project?.name)
  return (
    <DownloadMenu
      label="Download chart"
      options={[
        {
          label: 'Download as PNG',
          onSelect: () =>
            exportChartAsPNG(chartRef, `${baseFilename}.png`, {
              title: spec.title ?? undefined,
            }),
        },
        {
          label: 'Download as CSV',
          onSelect: () => downloadRowsCsv(exportRows, `${baseFilename}.csv`),
        },
        {
          label: 'Download as Excel',
          onSelect: () => downloadRowsXlsx(exportRows, `${baseFilename}.xlsx`),
        },
      ]}
    />
  )
}

// The rows behind a chart, for CSV / Excel export. Null for anything that
// isn't a real chart (a fallback listing or placeholder has nothing to
// export as a data table).
function chartExportRows(spec: ReportChartSpec): RowExport | null {
  if ((spec.type === 'bar' || spec.type === 'line') && spec.data?.length) {
    return {
      columns: [
        { key: 'date', label: 'Label' },
        { key: 'value', label: spec.title?.trim() || 'Value' },
      ],
      rows: spec.data,
      title: spec.title,
    }
  }
  if (spec.type === 'sankey' && spec.links?.length) {
    return {
      columns: [
        { key: 'source', label: 'From' },
        { key: 'target', label: 'To' },
        { key: 'value', label: 'Amount' },
      ],
      rows: labeledSankeyLinks(spec),
      title: spec.title,
    }
  }
  return null
}

function labeledSankeyLinks(spec: ReportChartSpec) {
  const labels = sankeyLabelsByName(spec.nodes ?? [])
  return (spec.links ?? []).map((link) => ({
    ...link,
    source: labels.get(link.source) ?? link.source,
    target: labels.get(link.target) ?? link.target,
  }))
}

// The shared chart components size themselves from their parent via a
// ResizeObserver (the `height` prop is not used for layout), so each
// chart needs an explicitly sized frame or it renders at 0px.
// Expanded, the dialog header carries the title and switch, and the
// frame grows to at least the dialog's height. `lead` is text that
// introduces the chart, shown just above it.
function ChartFrame({
  title,
  height,
  layout,
  lead,
  children,
}: {
  title?: string | null
  height: number
  layout: ChartLayout
  lead?: ReactNode
  children: ReactNode
}) {
  return (
    <>
      {title && !layout.expanded && (
        <figcaption className="mb-3 text-pretty font-semibold text-blue-7">
          {title}
        </figcaption>
      )}
      <ChartToolbar>{layout.toolbar}</ChartToolbar>
      <div className="relative">
        <div className={layout.emptyViewNote ? 'invisible' : undefined}>
          {lead}
          <div
            className={layout.expanded ? EXPANDED_MIN_HEIGHT_CLASS : undefined}
            style={{ height }}
          >
            {children}
          </div>
        </div>
        {layout.emptyViewNote && <EmptyViewNote note={layout.emptyViewNote} />}
      </div>
    </>
  )
}

function ChartToolbar({ children }: { children?: ReactNode }) {
  if (!children) return null
  return <div className="mb-3">{children}</div>
}

function renderChart(spec: ReportChartSpec, layout: ChartLayout) {
  if (spec.type === 'bar' || spec.type === 'line') {
    if (!spec.data || spec.data.length === 0)
      return <DataFallback spec={spec} />
    // Bar categories are account/merchant names that collide when drawn
    // in full, so they truncate; line charts are dated and stay as-is.
    return (
      <ChartFrame title={spec.title} height={BAR_LINE_HEIGHT} layout={layout}>
        {spec.type === 'line' ? (
          <LineChart
            data={spec.data}
            height={BAR_LINE_HEIGHT}
            title={spec.title ?? undefined}
            valueFormat={spec.value_format ?? 'currency'}
          />
        ) : (
          <BarChart
            data={spec.data}
            height={BAR_LINE_HEIGHT}
            title={spec.title ?? undefined}
            valueFormat={spec.value_format ?? 'currency'}
            truncateXLabels
            maxLabelLength={14}
          />
        )}
      </ChartFrame>
    )
  }

  if (spec.type === 'sankey') {
    const shown = chartSpecForView(spec, layout.view ?? DEFAULT_SANKEY_VIEW)
    const nodes = shown.nodes ?? []
    const links = resolveSankeyLinks(nodes, shown.links ?? [])
    // The switch stays on a fallback too, so a bad view can be left again.
    if (nodes.length === 0 || links.length === 0) {
      return (
        <>
          <ChartToolbar>{layout.toolbar}</ChartToolbar>
          <DataFallback spec={shown} />
        </>
      )
    }
    if (hasFlowOfFundsRoles(nodes)) return renderFlowOfFunds(spec, layout)
    if (hasSankeyCycle(nodes.length, links)) {
      return (
        <>
          <ChartToolbar>{layout.toolbar}</ChartToolbar>
          <Placeholder title={spec.title} reason="flow contains a cycle" />
        </>
      )
    }
    const height = sankeyChartHeight(nodes, links)
    return (
      <ChartFrame title={spec.title} height={height} layout={layout}>
        <SankeyChart
          data={{ nodes, links }}
          height={height}
          title={spec.title ?? undefined}
          valueFormat={spec.value_format ?? 'currency'}
        />
      </ChartFrame>
    )
  }

  return <DataFallback spec={spec} />
}

// Every view keeps the All view's size, so switching never moves the
// report below.
function renderFlowOfFunds(spec: ReportChartSpec, layout: ChartLayout) {
  const fullFlow = { nodes: spec.nodes ?? [], links: spec.links ?? [] }
  const view = layout.view ?? DEFAULT_SANKEY_VIEW
  const valueFormat = spec.value_format ?? 'currency'
  const summaryFor = (summaryView: SankeyView) =>
    flowSummaryForView(fullFlow, summaryView, valueFormat)
  return (
    <ChartFrame
      title={spec.title}
      height={flowChartHeight(fullFlow)}
      layout={layout}
      lead={
        <FlowSummary
          summary={summaryFor(view)}
          roomFor={FLOW_VIEWS.map(summaryFor)}
        />
      }
    >
      <FlowOfFundsChart
        flow={flowForView(fullFlow, view)}
        valueFormat={valueFormat}
      />
    </ChartFrame>
  )
}

// Unknown type or unrenderable data: show the underlying numbers as a
// compact list when we have them, else a quiet placeholder.
function DataFallback({ spec }: { spec: ReportChartSpec }) {
  const entries =
    spec.data?.map((point) => ({
      label: point.date,
      value: formatCell(point.value, spec.value_format ?? 'currency'),
    })) ??
    labeledSankeyLinks(spec).map((link) => ({
      label: `${link.source} → ${link.target}`,
      value: formatCell(link.value, spec.value_format ?? 'currency'),
    }))

  if (entries.length === 0) {
    return <Placeholder title={spec.title} reason="chart type not supported" />
  }
  return (
    <div className="rounded-md border p-4">
      {spec.title && (
        <p className="mb-2 text-sm font-medium text-foreground">{spec.title}</p>
      )}
      <dl className="space-y-1 text-sm">
        {entries.map((entry, index) => (
          // Labels can repeat once accounts show a short label.
          <div
            key={`${entry.label}-${index}`}
            className="flex justify-between gap-4"
          >
            <dt className="text-muted-foreground">{entry.label}</dt>
            <dd className="tabular-nums">{entry.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

function EmptyViewNote({ note }: { note: string }) {
  return (
    <p className="absolute inset-0 flex items-center justify-center rounded-md border border-dashed text-center text-sm text-muted-foreground">
      {note}
    </p>
  )
}

function Placeholder({
  title,
  reason,
}: {
  title?: string | null
  reason: string
}) {
  return (
    <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
      {title ? `${title} — ${reason}` : `Chart unavailable (${reason})`}
    </div>
  )
}
